import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import PermissionsSection from './PermissionsSection';
import { useTranslation } from '../../hooks/useTranslation';

const InviteMemberDialog = ({
  open,
  onClose,
  onSendInvitation,
  loading,
  initialRole = 'user'
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(initialRole);
  const {t} = useTranslation();
  const [expandedSections, setExpandedSections] = useState({
    sales: false,
    purchase: false,
    creditNote: false,
    debitNote: false,
    reports: false
  });
  const [permissions, setPermissions] = useState({
    sales: { enabled: true },
    purchase: { enabled: true },
    creditNote: { enabled: true },
    debitNote: { enabled: true },
    reports: { enabled: true }
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSendInvitation({ 
        email, 
        role,
        permissions: Object.entries(permissions).reduce((acc, [key, value]) => {
          acc[key] = {
            enabled: value.enabled,
            permissions: Object.entries(value)
              .filter(([k]) => k !== 'enabled')
              .reduce((pAcc, [k, v]) => {
                pAcc[k] = v;
                return pAcc;
              }, {})
          };
          return acc;
        }, {})
      });
    }
  };

  const validateForm = () => {
    // Implement form validation logic here
    return true; // Placeholder return, actual implementation needed
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          width: '500px',
          maxWidth: '500px',
          borderRadius: '4px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 2, 
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '48px'
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('Team_Members.Invite_Team_Member')}
        </Typography>
        <IconButton 
          onClick={onClose}
          size="small"
          sx={{ 
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        p: 3,
        '&.MuiDialogContent-root': {
          paddingTop: 3
        }
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            label={t('Team_Members.Email_Address')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('Team_Members.Enter_Email_Address')}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                height: '40px',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                }
              }
            }}
          />

          <FormControl fullWidth size="small">
            <InputLabel>{t('Team_Members.Role')}</InputLabel>
            <Select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              label={t('Team_Members.Role')}
              sx={{
                borderRadius: 0,
                height: '40px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                }
              }}
            >
          <MenuItem value="admin">{t('Team_Members.Admin')}</MenuItem>
<MenuItem value="manager">{t('Team_Members.Manager')}</MenuItem>
<MenuItem value="user">{t('Team_Members.User')}</MenuItem>
<MenuItem value="viewer">{t('Team_Members.Viewer')}</MenuItem>
            </Select>
          </FormControl>

          <PermissionsSection 
            role={role}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        borderTop: '1px solid',
        borderColor: 'divider',
        height: '56px',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1
      }}>
        <Button 
          onClick={onClose}
          size="small"
          sx={{ 
            textTransform: 'none',
            borderRadius: 0,
            minWidth: '80px',
            height: '32px'
          }}
        >
         {t('Common.Cancel')}
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={loading}
          sx={{ 
            textTransform: 'none',
            borderRadius: 0,
            minWidth: '120px',
            height: '32px'
          }}
        >
          {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
         {loading
  ? t('Team_Members.Sending')
  : t('Team_Members.Send_Invitation')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteMemberDialog; 